/**
 * TimelineGrid Component
 * Visual timeline grid showing capture slots as colored boxes
 * Green = received, Red = missed
 * Click received to open PhotoViewer, click missed to open status dialog
 */

import { useRef, useEffect, useState, useMemo, memo, useCallback } from 'react';
import { Empty, Spin } from 'antd';
import type { Photo, MissedCapture, TimelineEvent } from '@/types';
import { MaterialIcon } from '@/components/MaterialIcon';
import {
  mergePhotosAndMissed,
  groupTimelineSlotsForDisplay,
  formatSlotTime,
  type TimelineSlot,
  type TimelineDayGroup,
  type TimelineRow,
} from '@/utils/timelineUtils';
import styles from './TimelineGrid.module.css';

interface TimelineGridProps {
  photos: Photo[];
  missedCaptures: MissedCapture[];
  timelineEvents?: TimelineEvent[];
  loading: boolean;
  onPhotoClick: (photo: Photo, index: number) => void;
  onMissedClick: (missed: MissedCapture) => void;
  timezone?: string; // Device timezone (IANA format, e.g. "Europe/Rome")
}

// Slot dimensions (min 70, max 90, fills 100% width)
const MIN_SLOT_WIDTH = 70;
const MAX_SLOT_WIDTH = 90;
const GAP = 4;

interface SlotBoxProps {
  slot: TimelineSlot;
  onPhotoClick: (photo: Photo, index: number) => void;
  onMissedClick: (missed: MissedCapture) => void;
  photoIndex: number;
  timezone?: string;
}

/**
 * Single slot box component
 */
const SlotBox = memo(function SlotBox({
  slot,
  onPhotoClick,
  onMissedClick,
  photoIndex,
  timezone,
}: SlotBoxProps) {
  const isReceived = slot.type === 'received';
  const time = formatSlotTime(slot.expectedAt, timezone);

  const handleClick = useCallback(() => {
    if (isReceived && slot.photo) {
      onPhotoClick(slot.photo, photoIndex);
    } else if (!isReceived && slot.missedCapture) {
      onMissedClick(slot.missedCapture);
    }
  }, [isReceived, slot.photo, slot.missedCapture, onPhotoClick, onMissedClick, photoIndex]);

  return (
    <div
      className={`${styles.slotBox} ${isReceived ? styles.received : styles.missed}`}
      onClick={handleClick}
    >
      <span className={styles.slotTime}>{time}</span>
    </div>
  );
});

interface TimelineRowComponentProps {
  row: TimelineRow;
  columnCount: number;
  onPhotoClick: (photo: Photo, index: number) => void;
  onMissedClick: (missed: MissedCapture) => void;
  photoIndexMap: Map<string, number>;
  timezone?: string;
}

/**
 * Timeline row component
 * Uses CSS grid with fixed column count and 1fr to fill 100% width
 */
const TimelineRowComponent = memo(function TimelineRowComponent({
  row,
  columnCount,
  onPhotoClick,
  onMissedClick,
  photoIndexMap,
  timezone,
}: TimelineRowComponentProps) {
  return (
    <div
      className={styles.timelineRow}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
        gap: `${GAP}px`,
        paddingBottom: GAP,
      }}
    >
      {row.slots.map((slot) => {
        // Get photo index for navigation if this is a received slot
        const photoIndex = slot.photo
          ? photoIndexMap.get(slot.photo.photoId) ?? -1
          : -1;

        return (
          <SlotBox
            key={slot.expectedAt.toISOString()}
            slot={slot}
            onPhotoClick={onPhotoClick}
            onMissedClick={onMissedClick}
            photoIndex={photoIndex}
            timezone={timezone}
          />
        );
      })}
    </div>
  );
});

interface DayHeaderProps {
  group: TimelineDayGroup;
}

/**
 * Day header component (sticky)
 * Uses same .day-separator style from index.css as GalleryGridMode
 */
const DayHeader = memo(function DayHeader({ group }: DayHeaderProps) {
  return (
    <div className="day-separator">
      <span className="day-separator__date">{group.displayDate}</span>
      <span className="day-separator__count">
        • {group.receivedCount} received, {group.missedCount} missed
      </span>
    </div>
  );
});

interface EventRowProps {
  event: TimelineEvent;
}

/**
 * Event row component - full-width row showing timeline events
 */
const EventRow = memo(function EventRow({ event }: EventRowProps) {
  return (
    <div className={styles.eventRow}>
      <MaterialIcon name={event.icon} size={18} />
      <span className={styles.eventLabel}>{event.label}</span>
    </div>
  );
});

/**
 * TimelineGrid main component
 */
export function TimelineGrid({
  photos,
  missedCaptures,
  timelineEvents = [],
  loading,
  onPhotoClick,
  onMissedClick,
  timezone,
}: TimelineGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(1200);
  const [containerHeight, setContainerHeight] = useState(600);

  // Measure container dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerWidth(rect.width);
        const availableHeight = window.innerHeight - rect.top - 24;
        setContainerHeight(Math.max(400, availableHeight));
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Calculate column count to fill 100% width while keeping slots between min/max
  // Formula: find max columns where each slot is at least MIN_SLOT_WIDTH
  // Then verify each slot won't exceed MAX_SLOT_WIDTH
  const columnCount = useMemo(() => {
    // Max columns based on min slot width
    const maxCols = Math.floor((containerWidth + GAP) / (MIN_SLOT_WIDTH + GAP));
    // Min columns based on max slot width
    const minCols = Math.ceil((containerWidth + GAP) / (MAX_SLOT_WIDTH + GAP));
    // Use max columns (more compact), but ensure at least minCols
    return Math.max(4, Math.max(minCols, maxCols));
  }, [containerWidth]);

  // Merge photos and missed captures into timeline slots
  const timelineSlots = useMemo(() => {
    return mergePhotosAndMissed(photos, missedCaptures);
  }, [photos, missedCaptures]);

  // Group slots by day for display
  const groupedData = useMemo(() => {
    return groupTimelineSlotsForDisplay(timelineSlots, columnCount);
  }, [timelineSlots, columnCount]);

  // Group events by day for display
  const eventsByDay = useMemo(() => {
    const map = new Map<string, TimelineEvent[]>();
    for (const event of timelineEvents) {
      const date = event.timestamp;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;

      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      map.get(dateKey)!.push(event);
    }
    return map;
  }, [timelineEvents]);

  // Create photo index map for navigation
  // Maps photoId to its index in the photos array (sorted reverse chronologically)
  const photoIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    // Sort photos reverse chronologically (newest first) to match timeline order
    const sortedPhotos = [...photos].sort((a, b) => {
      const aTime = a.capturedAt?.toDate?.()?.getTime() ?? 0;
      const bTime = b.capturedAt?.toDate?.()?.getTime() ?? 0;
      return bTime - aTime;
    });
    sortedPhotos.forEach((photo, index) => {
      map.set(photo.photoId, index);
    });
    return map;
  }, [photos]);

  // Show loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  // Show empty state
  if (timelineSlots.length === 0) {
    return <Empty description="No captures in this period" className="py-20" />;
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <div
        ref={scrollContainerRef}
        className={styles.scrollContainer}
        style={{ height: containerHeight }}
      >
        {groupedData.groups.map((group) => {
          // Get events for this day and reverse to chronological order (oldest first)
          const dayEvents = [...(eventsByDay.get(group.dateKey) || [])].reverse();

          // Build interleaved list of rows and events at SLOT level
          // Timeline is chronological (oldest first at top, newest at bottom)
          // Events cause row breaks - we build rows dynamically
          type RenderItem =
            | { type: 'row'; slots: TimelineSlot[]; startIndex: number }
            | { type: 'event'; event: TimelineEvent; idx: number };

          const items: RenderItem[] = [];
          let eventIdx = 0;
          let currentRowSlots: TimelineSlot[] = [];
          let currentRowStartIndex = 0;

          // Flatten all slots from all rows for this day and reverse to chronological order
          const allDaySlots: TimelineSlot[] = [];
          let globalIdx = 0;
          for (const row of group.rows) {
            for (const slot of row.slots) {
              allDaySlots.push(slot);
            }
            if (globalIdx === 0) {
              globalIdx = row.startIndex;
            }
          }
          // Reverse to get oldest first
          allDaySlots.reverse();

          // Process slots one by one, inserting events between them
          for (let i = 0; i < allDaySlots.length; i++) {
            const slot = allDaySlots[i];
            const slotTime = slot.expectedAt.getTime();
            const nextSlot = allDaySlots[i + 1];
            const nextSlotTime = nextSlot?.expectedAt.getTime() ?? Infinity;

            // Add slot to current row
            if (currentRowSlots.length === 0) {
              currentRowStartIndex = globalIdx + i;
            }
            currentRowSlots.push(slot);

            // Check if any events should be inserted after this slot
            // Event should appear after slot if: slotTime < eventTime <= nextSlotTime
            // (Since we're going oldest to newest, event at 16:29 appears after 16:28 and before 16:30)
            while (eventIdx < dayEvents.length) {
              const event = dayEvents[eventIdx];
              const eventTime = event.timestamp.getTime();

              // Event belongs between this slot and next slot
              if (eventTime >= slotTime && eventTime < nextSlotTime) {
                // Finish current row first (if not empty)
                if (currentRowSlots.length > 0) {
                  items.push({
                    type: 'row',
                    slots: currentRowSlots,
                    startIndex: currentRowStartIndex,
                  });
                  currentRowSlots = [];
                }
                // Add the event
                items.push({ type: 'event', event, idx: eventIdx });
                eventIdx++;
              } else {
                break;
              }
            }

            // If current row is full, push it
            if (currentRowSlots.length >= columnCount) {
              items.push({
                type: 'row',
                slots: currentRowSlots,
                startIndex: currentRowStartIndex,
              });
              currentRowSlots = [];
            }
          }

          // Push any remaining slots as final row
          if (currentRowSlots.length > 0) {
            items.push({
              type: 'row',
              slots: currentRowSlots,
              startIndex: currentRowStartIndex,
            });
          }

          // Add any remaining events after all slots
          while (eventIdx < dayEvents.length) {
            items.push({ type: 'event', event: dayEvents[eventIdx], idx: eventIdx });
            eventIdx++;
          }

          return (
            <div key={group.dateKey} className={styles.dayGroup}>
              <DayHeader group={group} />
              <div className={styles.dayContent}>
                {items.map((item, itemIdx) => {
                  if (item.type === 'event') {
                    return (
                      <EventRow
                        key={`event-${item.event.timestamp.getTime()}-${item.idx}`}
                        event={item.event}
                      />
                    );
                  } else {
                    return (
                      <TimelineRowComponent
                        key={`row-${item.startIndex}-${itemIdx}`}
                        row={{ slots: item.slots, startIndex: item.startIndex }}
                        columnCount={columnCount}
                        onPhotoClick={onPhotoClick}
                        onMissedClick={onMissedClick}
                        photoIndexMap={photoIndexMap}
                        timezone={timezone}
                      />
                    );
                  }
                })}
              </div>
            </div>
          );
        })}

        {/* End marker */}
        <div className={styles.endMarker}>
          {timelineSlots.length} slots ({photos.length} received, {missedCaptures.length} missed)
        </div>
      </div>
    </div>
  );
}
